import { Box, Button, Link, SpaceBetween } from '@cloudscape-design/components';
import { Grid } from '@cloudscape-design/components';
import { Container } from '@cloudscape-design/components';
import { DistributorPublic, User } from '../common/types';
import { useDistributorAuth } from '../auth/useDistributorAuth';
import { useNavigate } from '@tanstack/react-router';
import { formatPhoneNumber } from '../common/formatting/phoneFormat';

export const SCORECARD_DEMO_CTA_HREF =
  'https://www.realtimefeedback.com/request-demo/?utm_source=scorecard&utm_medium=web&utm_campaign=scorecard_cta';

type ReportCardHeaderReportContext = {
  report_type?: string | null;
  place?: {
    created_via_distributor?: boolean | null;
  } | null;
  distributor?: DistributorPublic | null;
};

type ReportCardHeaderUserContext = User & {
  user_type?: string | null;
  distributor?: unknown;
};

type ReportCardHeaderProps = {
  user?: User;
  report?: ReportCardHeaderReportContext;
  distributor?: DistributorPublic;
  showDemoCta?: boolean;
};

function hasDistributorContext(user?: User, report?: ReportCardHeaderReportContext): boolean {
  const userContext = user as ReportCardHeaderUserContext | undefined;
  return (
    userContext?.user_type === 'distributor' ||
    userContext?.distributor != null ||
    report?.distributor != null ||
    report?.place?.created_via_distributor === true ||
    report?.report_type === 'distributor' ||
    report?.report_type === 'distributor_internal'
  );
}

export function ReportCardLogo({ compact = false }: { compact?: boolean }) {
  const { isAuthenticated } = useDistributorAuth();
  const height = compact ? 48 : 100;
  const navigate = useNavigate();
  return (
    <Link
      href={isAuthenticated ? '/' : '/create'}
      onFollow={() => {
        navigate({ to: isAuthenticated ? '/' : '/create' });
      }}
    >
      <img
        src="https://img.review-alerts.com/production/635fff10933d24ba71737b9f/635fff10933d24ba71737b9f/9fefeade-bc17-43a0-bfec-1d6e04768e5f.png"
        height={height}
        style={{ maxWidth: '350px', height: `${height}px` }}
        alt="Real Time Feedback Logo"
      />
    </Link>
  );
}

export default function ReportCardHeader(props: ReportCardHeaderProps) {
  const {
    user,
    report,
    distributor = report?.distributor ?? undefined,
    showDemoCta = true,
  } = props;
  const email = distributor?.email || user?.email || 'reputation@realtimefeedback.com';
  const phoneNumber = distributor?.phone_number || distributor?.additional_info;
  const shouldShowDemoCta = showDemoCta && !distributor && !hasDistributorContext(user, report);

  return (
    <Container>
      {shouldShowDemoCta && (
        <div
          className="flex items-center justify-between gap-3 sm:hidden"
          data-testid="report-card-header-compact"
        >
          <ReportCardLogo compact />
          <Button href={SCORECARD_DEMO_CTA_HREF} target="_blank" rel="noopener" variant="primary">
            Schedule a Demo
          </Button>
        </div>
      )}
      <div
        className={shouldShowDemoCta ? 'hidden sm:block' : undefined}
        data-testid="report-card-header-layout"
      >
        <Grid
          gridDefinition={[
            {
              colspan: {
                default: 12,
                xxs: 6,
              },
            },
            {
              colspan: {
                default: 12,
                xxs: 6,
              },
            },
          ]}
        >
          <div>
            <ReportCardLogo />
          </div>
          <div className="text-right" data-testid="report-card-header-contact-block">
            <SpaceBetween direction="vertical" size="xxxs">
              <Box variant="p" fontWeight="heavy" color="text-label">
                Contact us to schedule a demo:
              </Box>
              {shouldShowDemoCta && (
                <Box variant="p">
                  <Button
                    href={SCORECARD_DEMO_CTA_HREF}
                    target="_blank"
                    rel="noopener"
                    variant="primary"
                  >
                    Schedule a Demo
                  </Button>
                </Box>
              )}
              <Box variant="p">
                <Link href={`mailto:${email}`} color="inverted">
                  {email}
                </Link>
              </Box>
              <Box variant="p">
                <Link
                  href={`tel:${phoneNumber?.replace(/[^\d+]/g, '') || '1-888-294-1411'}`}
                  color="inverted"
                >
                  {phoneNumber ? formatPhoneNumber(phoneNumber) : '(888) 294-1411'}
                </Link>
              </Box>
            </SpaceBetween>
          </div>
        </Grid>
      </div>
    </Container>
  );
}
